import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { WhiteLabelPermissions } from "@lib/types/Context";
import { useQuery } from "@tanstack/react-query";
import { apiGetCustomer } from "@lib/api/customer";
import { Customer } from "@lib/types";
import { useParams } from "react-router-dom";

interface WhiteLabelContextType {
  // Customer data
  customer: Customer | null;
  setCustomer: (customer: Customer | null) => void;

  // Permissions
  permissions: WhiteLabelPermissions;

  // UI State
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;

  // Methods
  refreshCustomerData: () => Promise<void>;
  updateCustomerPermissions: (
    permissions: Partial<WhiteLabelPermissions>,
  ) => void;
}

const WhiteLabelContext = createContext<WhiteLabelContextType | undefined>(
  undefined,
);

interface WhiteLabelProviderProps {
  children: ReactNode;
  customerId?: string;
}

export const WhiteLabelProvider: React.FC<WhiteLabelProviderProps> = ({
  children,
  // customerId,
}) => {
  const params = useParams();
  const customerId = params.customerId;
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Move useQuery to component level
  const { data: customerData, isLoading: isQueryLoading } = useQuery({
    queryFn: () => apiGetCustomer(customerId!),
    queryKey: ["customer", customerId],
    enabled: !!customerId,
  });

  useEffect(() => {
    if (customerData?.data) {
      setCustomer(customerData.data);
    }
  }, [customerData]);

  const defaultPermissions: WhiteLabelPermissions = {
    canViewInvoices: true,
    canViewTransactions: true,
    canViewWalletPool: true,
    canManageManagers: true,
    canViewBlockchainPayments: true,
  };

  const refreshCustomerData = useCallback(async () => {
    if (!customerId) return;

    setIsLoading(true);
    try {
      // Query will be refetched automatically by react-query
      // console.log("🚀 ~ refreshCustomerData ~ customerId:", customerId);
    } catch (error) {
      console.error("Error fetching customer data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  const updateCustomerPermissions = useCallback(
    (_newPermissions: Partial<WhiteLabelPermissions>) => {
      if (customer) {
        setCustomer({
          ...customer,
          // permissions: {
          //   ...customer.permissions,
          //   ...newPermissions,
          // },
        });
      }
    },
    [customer],
  );

  const setIsLoadingCallback = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  const setCustomerCallback = useCallback((customer: Customer | null) => {
    setCustomer(customer);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<WhiteLabelContextType>(
    () => ({
      customer,
      setCustomer: setCustomerCallback,
      permissions: defaultPermissions,
      isLoading: isLoading || isQueryLoading,
      setIsLoading: setIsLoadingCallback,
      refreshCustomerData,
      updateCustomerPermissions,
    }),
    [
      customer,
      setCustomerCallback,
      defaultPermissions,
      isLoading,
      isQueryLoading,
      setIsLoadingCallback,
      refreshCustomerData,
      updateCustomerPermissions,
    ],
  );

  return (
    <WhiteLabelContext.Provider value={value}>
      {children}
    </WhiteLabelContext.Provider>
  );
};

export const useWhiteLabel = (): WhiteLabelContextType => {
  const context = useContext(WhiteLabelContext);
  if (context === undefined) {
    throw new Error("useWhiteLabel must be used within a WhiteLabelProvider");
  }
  return context;
};
